import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { cfetch } from '@confluence/network';
import {
	superbatchCSSRecorder,
	superbatchJSRecorder,
} from '@confluence/ssr-utilities/entry-points/SuperbatchResourceRecorder';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient';
import { stringifyForSSR } from '@confluence/ssr-utilities/entry-points/stringifyForSSR';

function convertResourceToScriptTags(resource: {
	url: string;
	resourceType: 'CSS' | 'JAVASCRIPT';
	ieOnly: boolean;
	key: string;
	batchType: string;
}) {
	const nonce = window.__SSR_NONCE_VALUE__;
	if (resource.resourceType === 'CSS') {
		return `<link rel="stylesheet" nonce="${nonce}" href="${resource.url}" data-key="${resource.key}" data-batch-type="${resource.batchType}"${resource.ieOnly ? ' data-ie-only="true"' : ''} />`;
	} else if (resource.resourceType === 'JAVASCRIPT') {
		return `<script nonce="${nonce}" src="${resource.url}" data-key="${resource.key}" data-batch-type="${resource.batchType}"${resource.ieOnly ? ' data-ie-only="true"' : ''}></script>`;
	}
	return '';
}

export const preloadConnectWebResources = async () => {
	if (
		!(
			fg('content_early_connect_iframe_initialization') ||
			expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)
		)
	) {
		return;
	}

	try {
		const response = await cfetch('/wiki/rest/webResources/1.0/resources', {
			method: 'POST',
			body: JSON.stringify({
				r: [
					'confluence.web.resources:querystring',
					'confluence.web.resources:navigator-context',
					'com.atlassian.plugins.atlassian-connect-plugin:confluence-atlassian-connect-resources-v5',
				],
				c: [],
				xc: ['_super'],
				xr: ['com.atlassian.plugins.jquery:jquery', 'com.atlassian.plugins.jquery:jquery-migrate'],
			}),
		});

		if (!response.ok) {
			throw new Error(`Failed to preload web resources: ${response.status}`);
		}

		const data = await response.json();

		// unparsedData in the response will later be claimed by window.WRM.data.claim()
		// This need to be fore all the JS scripts because they might claim the data while executing.
		if (data?.unparsedData) {
			const entries = [] as string[];
			for (const [key, value] of Object.entries(data.unparsedData)) {
				// value is a stringified JSON object.
				entries.push(`d["${key}"]=${stringifyForSSR(value)}`);
			}
			superbatchJSRecorder.addConnectResource(
				`<script>(function(d){if(d){${entries.join(';')}}})(window?.WRM?._unparsedData)</script>`,
			);
		}

		superbatchJSRecorder.addConnectResource(
			data?.resources
				?.filter((resource) => resource.resourceType === 'JAVASCRIPT')
				?.map((resource) => convertResourceToScriptTags(resource))
				.join('\n'),
		);

		superbatchCSSRecorder.addConnectResource(
			data?.resources
				?.filter((resource) => resource.resourceType === 'CSS')
				?.map((resource) => convertResourceToScriptTags(resource))
				.join('\n'),
		);
	} catch (error) {
		ssrPrepareErrorsClient.submitError(
			new Error(`Unable to preload connect web resources: ${error.message}`),
		);
	}
};
